<template>
  <BaseModal
    :title="`${capitalize($t('activerecord.models.shafts/job.one'))} #${renderShortId(job.id)}`"
    actions
  >
    <template #content>
      <div class="form__group">
        <FormRow
          :label="$t('shafts.issues.element_type')"
          :modifiers="['margin', 'narrow', 'lean', 'lateral']"
        >
          <div class="form-row__input">
            {{ $t(`shafts.element_types.${job.element_type.replace('/', '.')}`) }}
          </div>
        </FormRow>
      </div>
      <div class="form__group">
        <FormRow
          :label="$t('shafts.jobs.date')"
          :modifiers="['margin', 'narrow', 'lean', 'lateral']"
        >
          <div class="form-row__input">{{ formatDate(job.date) }}</div>
        </FormRow>
      </div>
      <div class="form__group">
        <FormRow
          :label="$t('shafts.jobs.maintenance_action')"
          :modifiers="['margin', 'narrow', 'lean', 'lateral']"
        >
          <div class="form-row__input">
            {{ $t(`shafts.maintenance_actions.${job.maintenance_action}`) }}
          </div>
        </FormRow>
      </div>
      <div class="form__group">
        <FormRow
          :label="$t('shafts.jobs.planner')"
          labelFor="planner"
          :modifiers="[
            'margin',
            'narrow',
            'lean',
            'lateral',
            errors && errors.planner && 'error-bg',
          ]"
        >
          <div class="form-row__input">
            <Loader v-if="loading" />
            <Select
              v-else
              v-model="plannerId"
              :placeholder="$t('shafts.jobs.select')"
              :groups="plannerOptions"
            />
          </div>
        </FormRow>
      </div>
    </template>

    <template #actions>
      <FormSubmitButton
        :buttonLabel="$t('shafts.jobs.send_to_planner')"
        @click="save"
      />
    </template>
  </BaseModal>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import I18n from 'i18n-js';

import BaseModal from '@/components/generic/base_modal.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import FormRow from '@/components/form/row.vue';
import Select from '@/components/form/select.vue';
import Loader from '@/components/generic/loader.vue';

import useAjax from '@/functions/use_ajax';
import { formatDate } from '@/functions/date_format_helpers';
import { renderShortId, capitalize } from '@/functions/utils';

const route = useRoute();
const ajax = useAjax();

const props = defineProps({
  job: { type: Object, required: true },
});

const emit = defineEmits(['save']);

const loading = ref(true);
const planners = ref(null);
const isSaving = ref(false);
const errors = ref(null);
const plannerId = ref(props.job.planner_id || null);

onMounted(async () => {
  loading.value = true;
  await loadPlanners();
  loading.value = false;
});

/**
 * Returns the correct text for the planner label
 */
const plannerSelectLabel = computed(() => {
  return planners.value?.planners.length > 0
    ? I18n.t('shafts.jobs.planners')
    : I18n.t('shafts.jobs.no_planners_assigned');
});

/**
 * Returns the label and items for the grouped select data
 */
const plannerOptions = computed(() => {
  if (loading.value) return [];

  return [
    {
      label: plannerSelectLabel.value,
      items: planners.value.planners.map((user) => ({
        label: user.full_name,
        value: user.id,
      })),
    },
    {
      label: I18n.t('shafts.jobs.other_users'),
      items: planners.value.other_users.map((user) => ({
        label: user.full_name,
        value: user.id,
      })),
    },
  ];
});

function shaftUrl() {
  const { organisationId, shaftId } = route.params;
  return `/organisations/${organisationId}/shafts/${shaftId}`;
}

async function loadPlanners() {
  const url = `${shaftUrl()}/planners`;

  let { ok, data } = await ajax(url);

  if (ok) {
    planners.value = data;
  }
}

async function save() {
  isSaving.value = true;

  const url = `${shaftUrl()}/jobs/${props.job.id}/transfers`;

  let { ok, data } = await ajax(url, {
    method: 'POST',
    body: { job: { planner_id: plannerId.value } },
  });

  if (ok) {
    emit('save', data);
  } else {
    isSaving.value = false;
    errors.value = data.job;
  }
}
</script>
